import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Search, Package, ChevronRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const STEPS = ["pending", "confirmed", "scooped", "shipped", "delivered"];
const STEP_LABELS = { pending: "Pending", confirmed: "Confirmed", scooped: "Scooped", shipped: "Shipped", delivered: "Delivered" };
const PACKAGE_NAMES = {
  duchess: "The Duchess", countess: "The Countess", princess: "The Princess",
  marquess: "The Marquess", empress: "The Empress", queen: "The Queen",
};

function StatusTimeline({ status }) {
  const activeIdx = STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-0 w-full mt-4" data-testid="order-status-timeline">
      {STEPS.map((step, i) => {
        const done = i <= activeIdx;
        const isCurrent = i === activeIdx;
        return (
          <div key={step} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                done
                  ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                  : "border-white/15 text-white/20"
              } ${isCurrent ? "ring-2 ring-[#D4AF37]/30 ring-offset-2 ring-offset-[#0a0a0a]" : ""}`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="text-[10px] font-montserrat">{i + 1}</span>}
              </div>
              <span className={`text-[9px] sm:text-[10px] font-montserrat mt-1.5 tracking-wider ${done ? "text-[#D4AF37]" : "text-white/20"}`}>
                {STEP_LABELS[step]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 transition-all duration-500 ${i < activeIdx ? "bg-[#D4AF37]/50" : "bg-white/10"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function OrderTracker() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    setLoading(true);
    setOrders(null);
    try {
      const { data } = await axios.post(`${API}/orders/track`, { email: email.trim() });
      setOrders(data);
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#D4AF37]/10 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href="/" data-testid="tracker-logo-link">
            <img src={LOGO_URL} alt="Vicki Ann's" className="h-12" />
          </a>
          <a href="/" className="font-montserrat text-white/30 text-xs tracking-wider hover:text-[#D4AF37] transition-colors">
            Back to Shop
          </a>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-6 pt-16 pb-20">
        <div className="w-full max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <div className="w-14 h-14 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5">
              <Package className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h1 className="font-playfair text-[#D4AF37] text-3xl sm:text-4xl font-bold mb-3">Track Your Order</h1>
            <p className="font-montserrat text-white/40 text-sm">Enter the email you used when placing your order</p>
          </motion.div>

          <motion.form
            onSubmit={handleTrack}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex gap-3 mb-10"
            data-testid="track-order-form"
          >
            <Input
              data-testid="track-email-input"
              type="email"
              className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-12 font-montserrat placeholder:text-white/30 flex-1"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="track-submit-btn"
              className="btn-gold px-6 h-12 rounded-lg text-sm font-montserrat flex items-center gap-2 disabled:opacity-50 shrink-0"
            >
              <Search className="w-4 h-4" />
              {loading ? "..." : "Track"}
            </button>
          </motion.form>

          {error && (
            <div className="bg-red-900/20 border border-red-500/20 text-red-300 text-sm px-4 py-3 rounded-lg font-montserrat mb-6" data-testid="track-error">
              {error}
            </div>
          )}

          {orders !== null && orders.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
              data-testid="track-no-results"
            >
              <p className="font-montserrat text-white/30 text-sm">No orders found for this email address.</p>
              <a href="/#order" className="font-montserrat text-[#D4AF37]/60 text-xs mt-3 inline-block hover:text-[#D4AF37] transition-colors">
                Place your first order <ChevronRight className="w-3 h-3 inline" />
              </a>
            </motion.div>
          )}

          {orders && orders.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
              data-testid="track-results"
            >
              <p className="font-montserrat text-white/40 text-xs tracking-wider mb-4">
                {orders.length} order{orders.length > 1 ? "s" : ""} found
              </p>
              {orders.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="glass-card border border-[#D4AF37]/10 rounded-xl p-5 sm:p-6"
                  data-testid={`track-order-${order.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-playfair text-white text-lg font-semibold">
                      {PACKAGE_NAMES[order.package_id] || order.package_id}
                    </h3>
                    <span className="font-montserrat text-white/20 text-[10px]">
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-montserrat text-white/30 text-xs mb-1">{order.customer_name}</p>
                  <StatusTimeline status={order.status} />
                  {order.tracking_number && (order.status === "shipped" || order.status === "delivered") && (
                    <div className="mt-4 bg-[#E8923A]/10 border border-[#E8923A]/20 rounded-lg p-3" data-testid={`tracking-number-${order.id}`}>
                      <p className="font-montserrat text-[#E8923A]/60 text-[10px] tracking-wider uppercase mb-0.5">Tracking Number</p>
                      <p className="font-montserrat text-[#E8923A] text-sm font-bold">{order.tracking_number}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
